import React, { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { RegistrationForm } from '../components/RegistrationForm';
import { BrandsMenu } from '../components/BrandsMenu';
import { RecurringLogin } from '../components/RecurringLogin';
import { useStore, findEventByCode, getDropWindowStatus } from '../lib/store';
import { KeyRound, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
interface LandingPageProps {
  onRegisterSuccess?: () => void;
  onLogin?: () => void;
}
export function LandingPage({
  onRegisterSuccess,
  onLogin
}: LandingPageProps) {
  const {
    events,
    setActiveEventId,
    resolveDropByCode
  } = useStore();
  const [hasCode, setHasCode] = useState(false);
  const [staffCode, setStaffCode] = useState('');
  const [matchedDrop, setMatchedDrop] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  // A drop matched but is outside its date/time window — message the user.
  const [windowMsg, setWindowMsg] = useState<string | null>(null);
  // Open a matched drop ONLY if it's live now (inside its admin date/time
  // window). Returns true when access was granted.
  const openDropIfActive = (drop: (typeof events)[number]): boolean => {
    const status = getDropWindowStatus(drop);
    if (status !== 'active') {
      setWindowMsg(status === 'off' ? `${drop.title} is currently turned off. Check back soon.` : status === 'before' ? `${drop.title} hasn't started yet. It opens ${drop.date} ${drop.time}.` : status === 'after' ? `${drop.title} has ended (${drop.date} ${drop.time}). This drop is closed.` : `${drop.title} doesn't have a valid time set yet. Check with the staff.`);
      return false;
    }
    setWindowMsg(null);
    setActiveEventId(drop.id);
    setMatchedDrop(drop.title);
    setHasCode(true);
    setError(false);
    return true;
  };
  // QR deep-link: a drop's QR encodes "?drop=<CODE>". When someone scans it we
  // match the drop and jump straight into registration, skipping the manual
  // code-entry step. We retry once events have hydrated from the backend.
  useEffect(() => {
    if (hasCode) return;
    const params = new URLSearchParams(window.location.search);
    const qrCode = params.get('drop') || params.get('code');
    if (!qrCode) return;
    const drop = findEventByCode(events, qrCode);
    if (drop) openDropIfActive(drop);
    // Not in the local list yet — resolve it live from the backend.
    else resolveDropByCode(qrCode).then(d => d && openDropIfActive(d));
  }, [events, hasCode]);
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Entry is gated by a REAL drop code. Resolve it against the local list AND
    // a live backend read, so ANY valid code always works — even one that
    // hasn't reached this device via the background poll yet.
    setError(false);
    setCheckingCode(true);
    let drop = findEventByCode(events, staffCode);
    if (!drop) {
      const resolved = await resolveDropByCode(staffCode);
      drop = resolved || undefined;
    }
    setCheckingCode(false);
    if (!drop) {
      setError(true);
      setWindowMsg(null);
      return;
    }
    openDropIfActive(drop);
  };
  return <main className="min-h-screen w-full bg-brand-ice relative overflow-hidden flex flex-col items-center" data-id="element-2183" data-mp="kfh4mhxybatz">
      {/* Brands & organizers menu — top-left */}
      <div className="absolute top-4 left-4 z-30" data-id="element-2184" data-mp="bdrcf9w8w7v5">
        <BrandsMenu variant="light" data-id="element-2185" data-mp="3stppf3u8exbl" />
      </div>

      {/* Returning-member login — bottom-right */}
      <RecurringLogin onLogin={onLogin ?? onRegisterSuccess} data-id="element-2186" data-mp="1mwpe3b6d48n" />

      {/* Quiet ice-blue backdrop keeps the task flow focused. */}
      <div className="absolute top-0 left-0 w-full h-[50vh] bg-brand-lavender/45 pointer-events-none" data-id="element-2187" data-mp="3jtuaqp1gwk6t" />

      {/* Ambient floating fish tying into the logo motif */}
      <div className="absolute top-16 -left-4 text-brand-purple/10 animate-[pulse_4s_ease-in-out_infinite]" data-id="element-2188" data-mp="3gqwuk7v2j4vh">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" className="-scale-x-100 transform rotate-12" data-id="element-2189" data-mp="3brjwss2nsde4">
          <path d="M12 2C11 2 10 2.5 9 3C7 4 5 6 3 8C2 9 1 10 1 12C1 14 2 15 3 16C5 18 7 20 9 21C10 21.5 11 22 12 22C14 22 16 21 18 19C19 18 20 17 21 15L23 16V8L21 9C20 7 19 6 18 5C16 3 14 2 12 2ZM18 10C18.6 10 19 10.4 19 11C19 11.6 18.6 12 18 12C17.4 12 17 11.6 17 11C17 10.4 17.4 10 18 10Z" data-id="element-2190" data-mp="322a2r6o98knd" />
        </svg>
      </div>
      <div className="absolute top-32 -right-8 text-brand-purple/10 animate-[bounce_5s_ease-in-out_infinite]" data-id="element-2191" data-mp="2o1zbi2j1941o">
        <svg width="96" height="96" viewBox="0 0 24 24" fill="currentColor" className="transform -rotate-12" data-id="element-2192" data-mp="2j4hbx2qwo16c">
          <path d="M12 2C11 2 10 2.5 9 3C7 4 5 6 3 8C2 9 1 10 1 12C1 14 2 15 3 16C5 18 7 20 9 21C10 21.5 11 22 12 22C14 22 16 21 18 19C19 18 20 17 21 15L23 16V8L21 9C20 7 19 6 18 5C16 3 14 2 12 2ZM18 10C18.6 10 19 10.4 19 11C19 11.6 18.6 12 18 12C17.4 12 17 11.6 17 11C17 10.4 17.4 10 18 10Z" data-id="element-2193" data-mp="hcaxj4ylgpog" />
        </svg>
      </div>

      <div className="w-full max-w-md flex flex-col items-center relative z-10 pt-12 px-4 sm:px-6 lg:px-8 pb-8" data-id="element-2194" data-mp="yl3qk62n0m9f">
        {/* Integrated Logo Area */}
        <div className="w-72 max-w-full mb-8 relative group" data-id="element-2195" data-mp="13mkphw1k9ynd">
          <div className="absolute inset-0 bg-white/40 blur-3xl rounded-full transition-all duration-500 group-hover:bg-white/60" data-id="element-2196" data-mp="6v8za3v7ymi4" />
          <Logo variant="light" className="relative z-10 drop-shadow-sm transform transition-all duration-500 hover:scale-105 hover:-translate-y-1" data-id="element-2197" data-mp="axvm1qd19h8g" />
        </div>

        {/* Overlapping Card */}
        <div className="w-full bg-white rounded-[2rem] p-6 shadow-[0_20px_48px_rgba(22,38,95,0.12)] border border-white animate-in slide-in-from-bottom-8 duration-700" data-id="element-2198" data-mp="1rab0003mqw9g">
          {!hasCode ? <div className="text-center" data-id="element-2199" data-mp="16kqak9714j6e">
              <div className="w-16 h-16 bg-brand-lavender rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner" data-id="element-2200" data-mp="16jox5y2jjkli">
                <KeyRound className="w-8 h-8 text-brand-purple" data-id="element-2201" data-mp="11nzyry29c9qs" />
              </div>
              <h1 className="text-3xl font-display text-brand-navy mb-2" data-id="element-2202" data-mp="1giegzwdzzf4e">
                Get your catch!
              </h1>
              <p className="text-gray-500 text-sm mb-6" data-id="element-2203" data-mp="nuic44e9ygwj">
                Enter the drop code from the staff at the location to register
                your catch.
              </p>

              <form onSubmit={handleCodeSubmit} className="space-y-4" data-id="element-2204" data-mp="itb5i8nvbnrg">
                <Input placeholder="ENTER DROP CODE" value={staffCode} onChange={e => {
              setStaffCode(e.target.value.toUpperCase());
              if (error) setError(false);
              if (windowMsg) setWindowMsg(null);
            }} className="text-center text-2xl tracking-widest uppercase font-mono" autoFocus data-id="element-2205" data-mp="xl3johbxevrl" />
                {error && <p className="text-red-500 text-xs" data-id="element-2206" data-mp="2gjrpnd8qbkb8">
                    That drop code isn't active. Check with the staff and try
                    again.
                  </p>}
                {windowMsg && <p className="text-amber-600 text-xs font-medium" data-id="element-2207" data-mp="26r3vkq94vug7">
                    {windowMsg}
                  </p>}
                <Button type="submit" className="w-full" disabled={checkingCode || !staffCode.trim()} data-id="element-2208" data-mp="2zidpkosif5dk">
                  {checkingCode ? <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-2209" data-mp="2pi3eb9q1c811" />
                      Checking…
                    </> : 'Register Catch'}
                </Button>
              </form>
            </div> : <>
              {/* Minimal transition — let the form handle all messaging */}
              <div className="flex items-center justify-center mb-4" data-id="element-2210" data-mp="20ry3r66xi0et">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full flex items-center" data-id="element-2211" data-mp="1wr4f2alnwhkv">
                  <Check className="w-3 h-3 mr-1" data-id="element-2212" data-mp="1rt2z3yjj8ydm" />
                  {matchedDrop ? `${matchedDrop} unlocked` : 'Code approved'}
                </span>
              </div>
              <RegistrationForm onSuccess={onRegisterSuccess} isWalkIn={true} data-id="element-2213" data-mp="2kgyumzxd8k8t" />
            </>}
        </div>

        <p className="text-brand-navy/60 text-xs text-center max-w-xs" data-id="element-2214" data-mp="2fpqk20kausu2">
          By registering, you agree to receive in-app updates about future
          drops. We never send SMS — your number is only used to recognise you.
        </p>
      </div>
    </main>;
}